import { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { Button } from 'blaze-ui/Button'
import { Close } from 'blaze-ui/icons'
import { Credits } from 'blaze-ui/icons/20/Credits'
import { YouTubeOutline } from 'blaze-ui/icons/20'
import { Image03 } from 'blaze-ui/icons/24'
import { trackEventInHeap, HeapEventType } from 'utils/heapTracking'
import { useCreditUsage } from 'hooks/useCreditUsage'
import Styles from '../../VisualStylesForExistingUsersModal.module.scss'

interface MobileIntroStepProps {
  onNext: () => void
  onClose: () => void
}

export const MobileIntroStep = observer(({ onNext, onClose }: MobileIntroStepProps) => {
  const { usageCosts } = useCreditUsage()

  useEffect(() => {
    trackEventInHeap(HeapEventType.VisualStylesForExistingUsers, {
      action: 'view-intro-step-mobile',
    })
  }, [])

  return (
    <div className={Styles.mobileStepContainer}>
      <div className={Styles.mobileStepHeader}>
        <span className={Styles.mobileStepHeaderTitle}>What's New</span>
        <Button size="sm" variant="tertiary" onPress={onClose}>
          <Close size={20} />
        </Button>
      </div>

      <div className={Styles.mobileStepContent}>
        <div className={Styles.mobileIntroContent}>
          <img
            src="https://res.cloudinary.com/almanac/image/upload/v1767967966/blaze_assets/content_preferences/Frame_1171275087_a5vuuf.webp"
            alt="Visual Styles Introduction"
            className={Styles.heroImage}
          />

          <div className={Styles.mobileIntroHeading}>
            <Heading level={2}>Turn on stylized images and videos to create on-brand content that performs.</Heading>
          </div>

          <div className={Styles.mobileFeaturesStack}>
            <div className={Styles.mobileFeatureCard}>
              <div className={Styles.mobileFeatureCardHeader}>
                <Image03 size={20} color="var(--dark-80)" />
                <Heading level={4}>Stylized Images</Heading>
              </div>
              <Text color="var(--dark-80)" variant="secondary">
                Pick a professional-grade Visual Style to keep every image consistent.
              </Text>
              <div className={Styles.mobileFeatureCredits}>
                <Credits size={12} color="var(--dark-40)" />
                <Text variant="metadata" color="var(--dark-40)">
                  {usageCosts?.visual_styles_credits_cost} credits per image
                </Text>
              </div>
            </div>

            <div className={Styles.mobileFeatureCard}>
              <div className={Styles.mobileFeatureCardHeader}>
                <YouTubeOutline size={20} color="var(--dark-80)" />
                <Heading level={4}>AI Videos</Heading>
              </div>
              <Text color="var(--dark-80)" variant="secondary">
                Turn static images into compelling 8-second videos.
              </Text>
              <div className={Styles.mobileFeatureCredits}>
                <Credits size={12} color="var(--dark-40)" />
                <Text variant="metadata" color="var(--dark-40)">
                  {usageCosts?.video_credits_cost} credits per video
                </Text>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={Styles.mobileFooter}>
        <Button variant="primary" size="lg" onPress={onNext} className={Styles.mobileFullWidthButton}>
          Enable and Pick My Visual Style
        </Button>
      </div>
    </div>
  )
})
